import React, { useState, useEffect, useCallback } from 'react';
import { taskAPI } from '../../api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ListTodo, Plus, Play, Pause, Clock, Trash2, Edit2, Timer, CheckCircle } from 'lucide-react';
import { format, subDays } from 'date-fns';
import { toast } from 'sonner';

const MyTasksPage = () => {
  const [tasks, setTasks] = useState([]);
  const [taskLogs, setTaskLogs] = useState([]);
  const [activeTask, setActiveTask] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [newTaskName, setNewTaskName] = useState('');
  const [editingTask, setEditingTask] = useState(null);
  const [editTaskName, setEditTaskName] = useState('');
  
  // Filter states
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  
  // Timer state
  const [elapsedTime, setElapsedTime] = useState(0);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [tasksRes, activeRes, logsRes] = await Promise.all([
        taskAPI.getMyTasks(),
        taskAPI.getActiveTask(),
        taskAPI.getLogs(startDate, endDate)
      ]);
      setTasks(tasksRes.data);
      setActiveTask(activeRes.data);
      setTaskLogs(logsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Timer effect for active task
  useEffect(() => {
    let interval;
    if (activeTask?.start_time) {
      const startTime = new Date(activeTask.start_time);
      interval = setInterval(() => {
        const now = new Date();
        const elapsed = Math.floor((now - startTime) / 1000);
        setElapsedTime(elapsed);
      }, 1000);
    } else {
      setElapsedTime(0);
    }
    return () => clearInterval(interval);
  }, [activeTask]);

  const handleCreateTask = async () => {
    if (!newTaskName.trim()) {
      toast.error('Please enter a task name');
      return;
    }
    
    try {
      await taskAPI.create({ name: newTaskName.trim() });
      toast.success('Task created');
      setCreateDialogOpen(false);
      setNewTaskName('');
      loadData();
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error('Failed to create task');
    }
  };

  const handleUpdateTask = async () => {
    if (!editTaskName.trim() || !editingTask) {
      toast.error('Please enter a task name');
      return;
    }
    
    try {
      await taskAPI.update(editingTask.id, { name: editTaskName.trim() });
      toast.success('Task updated');
      setEditDialogOpen(false);
      setEditingTask(null);
      setEditTaskName('');
      loadData();
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await taskAPI.delete(taskId);
      toast.success('Task deleted');
      loadData();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };

  const handleSwitchTask = async (taskId) => {
    try {
      await taskAPI.switchTask(taskId);
      toast.success('Task switched');
      loadData();
    } catch (error) {
      console.error('Error switching task:', error);
      toast.error('Failed to switch task');
    }
  };

  const handleStopTask = async () => {
    try {
      await taskAPI.stopTask();
      toast.success('Task stopped');
      loadData();
    } catch (error) {
      console.error('Error stopping task:', error);
      toast.error('Failed to stop task');
    }
  };

  const openEditDialog = (task) => {
    setEditingTask(task);
    setEditTaskName(task.name);
    setEditDialogOpen(true);
  };

  const formatDuration = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatMinutes = (minutes) => {
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hrs > 0) {
      return `${hrs}h ${mins}m`;
    }
    return `${mins}m`;
  };

  // Group logs by task for summary
  const taskSummary = taskLogs.reduce((acc, log) => {
    const taskName = log.task_name;
    if (!acc[taskName]) {
      acc[taskName] = { total_minutes: 0, count: 0 };
    }
    acc[taskName].total_minutes += log.duration_minutes || 0;
    acc[taskName].count += 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="my-tasks-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">My Tasks</h1>
          <p className="text-muted-foreground">Manage your tasks and track time</p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="create-task-btn">
              <Plus className="h-4 w-4" />
              New Task
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Task</DialogTitle>
              <DialogDescription>Add a new task to your list</DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label htmlFor="taskName">Task Name</Label>
              <Input 
                id="taskName"
                value={newTaskName}
                onChange={(e) => setNewTaskName(e.target.value)}
                placeholder="Enter task name"
                className="mt-2"
                data-testid="task-name-input"
                onKeyDown={(e) => e.key === 'Enter' && handleCreateTask()}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateTask} data-testid="confirm-create-task-btn">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Active Task Card */}
      <Card className={activeTask ? 'border-green-500 bg-green-50' : ''}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Timer className="h-5 w-5" />
            Active Task
          </CardTitle>
          <CardDescription>
            {activeTask ? 'Currently tracking time' : 'No active task - select a task to start tracking'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {activeTask ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-200 rounded-lg">
                  <Play className="h-6 w-6 text-green-700" />
                </div>
                <div>
                  <p className="text-xl font-bold">{activeTask.task_name}</p>
                  <p className="text-sm text-muted-foreground">
                    Started at {format(new Date(activeTask.start_time), 'h:mm a')}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-3xl font-mono font-bold text-green-700">{formatDuration(elapsedTime)}</p>
                  <p className="text-sm text-muted-foreground">Elapsed Time</p>
                </div>
                <Button 
                  variant="destructive" 
                  onClick={handleStopTask}
                  className="gap-2"
                  data-testid="stop-task-btn"
                >
                  <Pause className="h-4 w-4" />
                  Stop
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-4 text-muted-foreground">
              <Clock className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>Click &quot;Start&quot; on any task below to begin tracking time</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tasks List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ListTodo className="h-5 w-5" />
            My Task List ({tasks.length})
          </CardTitle>
          <CardDescription>Your personal tasks for time tracking</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : tasks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <ListTodo className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No tasks yet. Create your first task to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Task Name</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tasks.map((task) => (
                  <TableRow key={task.id} data-testid="task-row">
                    <TableCell className="font-medium">{task.name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(task.created_at), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell>
                      {activeTask?.task_id === task.id ? (
                        <Badge variant="default" className="bg-green-500 gap-1">
                          <Play className="h-3 w-3" /> Active
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Idle</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {activeTask?.task_id !== task.id && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleSwitchTask(task.id)}
                            className="gap-1"
                            data-testid="start-task-btn"
                          >
                            <Play className="h-3 w-3" /> Start
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => openEditDialog(task)}
                          data-testid="edit-task-btn"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteTask(task.id)}
                          data-testid="delete-task-btn"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Task Summary */}
      {Object.keys(taskSummary).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5" />
              Time Summary
            </CardTitle>
            <CardDescription>Time spent on tasks in the selected period</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 mb-4">
              <div className="space-y-2">
                <Label>From</Label>
                <Input 
                  type="date" 
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-40"
                />
              </div>
              <div className="space-y-2">
                <Label>To</Label>
                <Input 
                  type="date" 
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-40"
                />
              </div>
              <div className="flex items-end">
                <Button variant="outline" onClick={loadData}>Apply</Button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(taskSummary).map(([taskName, data]) => (
                <div key={taskName} className="p-4 border rounded-lg bg-muted/50">
                  <p className="font-medium truncate" title={taskName}>{taskName}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-2xl font-bold">{formatMinutes(data.total_minutes)}</span>
                    <Badge variant="outline">{data.count} sessions</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Task Logs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Task History ({taskLogs.length} entries)
          </CardTitle>
          <CardDescription>Detailed log of time entries</CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[300px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Task</TableHead>
                  <TableHead>Start Time</TableHead>
                  <TableHead>End Time</TableHead>
                  <TableHead className="text-right">Duration</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {taskLogs.map((log) => (
                  <TableRow key={log.id} data-testid="task-log-row">
                    <TableCell>{log.date}</TableCell>
                    <TableCell className="font-medium">{log.task_name}</TableCell>
                    <TableCell>
                      {log.start_time ? format(new Date(log.start_time), 'h:mm a') : '-'}
                    </TableCell>
                    <TableCell>
                      {log.end_time ? format(new Date(log.end_time), 'h:mm a') : (
                        <Badge variant="outline" className="text-green-600">In Progress</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {log.end_time ? formatMinutes(log.duration_minutes) : '-'}
                    </TableCell>
                  </TableRow>
                ))}
                {taskLogs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                      No task logs found for the selected period
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Edit Task Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Task</DialogTitle>
            <DialogDescription>Update the task name</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="editTaskName">Task Name</Label>
            <Input 
              id="editTaskName"
              value={editTaskName}
              onChange={(e) => setEditTaskName(e.target.value)}
              placeholder="Enter task name"
              className="mt-2"
              onKeyDown={(e) => e.key === 'Enter' && handleUpdateTask()}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateTask}>Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyTasksPage;
